import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Activity, CalendarDays, Inbox, PlaneTakeoff, Repeat, FileText, Users, LogOut, Menu } from "lucide-react";
import MasterSchedule from "../components/MasterSchedule";
import { ShiftsProvider } from "../context/ShiftsContext";
import OpenShifts from "../components/OpenShifts";
import TimeOff from "../components/TimeOff";
import SwapRequests from "../components/SwapRequests";
import PolicyEditor from "../components/PolicyEditor";
import StaffManagement from "../components/StaffManagement";
import { Button } from "../components/ui/button";

const NAV = [
  { key: "schedule", label: "Master Schedule", icon: CalendarDays, testid: "nav-master-schedule-tab" },
  { key: "open", label: "Open Shifts", icon: Inbox, testid: "nav-open-shifts-tab" },
  { key: "timeoff", label: "Time Off", icon: PlaneTakeoff, testid: "nav-time-off-tab" },
  { key: "swaps", label: "Shift Swaps", icon: Repeat, testid: "nav-swap-requests-tab" },
  { key: "policy", label: "Policy & Notes", icon: FileText, testid: "nav-policy-notes-doc-tab" },
  { key: "staff", label: "Staff", icon: Users, testid: "nav-staff-management-tab", adminOnly: true },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("schedule");
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user.role === "admin";
  const nav = NAV.filter((n) => !n.adminOnly || isAdmin);

  const initials = user.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const SideNav = (
    <nav className="flex flex-col gap-1 p-4">
      {nav.map((n) => {
        const Icon = n.icon;
        const active = tab === n.key;
        return (
          <button
            key={n.key}
            data-testid={n.testid}
            onClick={() => { setTab(n.key); setMobileOpen(false); }}
            className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              active ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Icon className="h-4.5 w-4.5" strokeWidth={active ? 2.4 : 2} />
            {n.label}
          </button>
        );
      })}
    </nav>
  );

  return (
    <ShiftsProvider>
    <div className="min-h-screen bg-slate-50">
      {/* Topbar */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="flex items-center justify-between px-4 sm:px-6 h-16">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-600" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle">
              <Menu className="h-6 w-6" />
            </button>
            <div className="h-9 w-9 rounded-xl bg-slate-900 flex items-center justify-center text-white">
              <Activity className="h-5 w-5" />
            </div>
            <span className="text-lg font-extrabold tracking-tight text-slate-900 font-display">RadiantShift</span>
            <span className="hidden sm:inline text-xs font-mono uppercase tracking-wider text-slate-400 border-l border-slate-200 pl-3">CT Radiology</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-slate-900 leading-tight" data-testid="current-user-name">{user.name}</div>
              <div className="text-xs text-slate-500 capitalize">{user.role === "admin" ? "Super User" : user.title || "Staff"}</div>
            </div>
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white flex items-center justify-center text-xs font-bold">
              {initials}
            </div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" className="text-slate-500 hover:text-rose-600">
              <LogOut className="h-4.5 w-4.5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-4rem)] sticky top-16 self-start">
          {SideNav}
        </aside>
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-20 top-16">
            <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl">{SideNav}</div>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 max-w-[1700px]">
          {tab === "schedule" && <MasterSchedule user={user} isAdmin={isAdmin} />}
          {tab === "open" && <OpenShifts isAdmin={isAdmin} />}
          {tab === "timeoff" && <TimeOff isAdmin={isAdmin} />}
          {tab === "swaps" && <SwapRequests user={user} isAdmin={isAdmin} />}
          {tab === "policy" && <PolicyEditor isAdmin={isAdmin} />}
          {tab === "staff" && isAdmin && <StaffManagement currentUser={user} />}
        </main>
      </div>
    </div>
    </ShiftsProvider>
  );
}
